import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Progress, Spin, Select, Button,
         Alert, Badge, Tooltip, message } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, TeamOutlined,
         CalendarOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import SurveyService from '../../services/SurveyService';

const { Option } = Select;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function QoCSurveyProgress({ survey, onClose }) {
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [filterCol,  setFilterCol]  = useState(null);
    const [filterReg,  setFilterReg]  = useState(null);

    useEffect(() => { load(); }, []); // eslint-disable-line

    const load = () => {
        setLoading(true);
        SurveyService.getProgress(survey.id)
            .then(r => setData(r.data?.data))
            .catch(e => message.error(e.message))
            .finally(() => setLoading(false));
    };

    if (loading) return <div style={{textAlign:'center',padding:40}}><Spin size="large"/></div>;
    if (!data)   return <Alert type="error" message="Failed to load progress data"/>;

    const {
        total_facilities, submitted_count, draft_count, coverage_pct,
        days_remaining, pace_needed, daily_progress, collectors,
        recent_responses, pending_facilities
    } = data;

    const onTrack = pace_needed !== null && submitted_count > 0
        ? (Object.values(daily_progress).reduce((a,b)=>a+b,0) / Object.keys(daily_progress).length) >= pace_needed
        : null;

    // Daily chart
    const dailyDates = Object.keys(daily_progress).sort();
    const dailyCounts = dailyDates.map(d => daily_progress[d]);
    const chartOpt = {
        tooltip:{ trigger:'axis' },
        xAxis:{ type:'category', data: dailyDates, axisLabel:{formatter: v => v.slice(5)} },
        yAxis:{ type:'value', minInterval:1 },
        series:[{
            type:'bar', data: dailyCounts, barWidth:'60%',
            itemStyle:{ color:'#1890ff', borderRadius:[4,4,0,0] },
            label:{ show:true, position:'top', fontSize:11, fontWeight:'bold' },
        }],
        grid:{ left:40, right:20, top:20, bottom:40 },
    };

    const collectorCols = [
        { title:'Collector', render:(_,r)=>(
            <div>
                <div style={{fontWeight:700}}>{r.name}</div>
                <div style={{fontSize:11,color:'#888'}}>{r.email}</div>
            </div>
        )},
        { title:'Region', dataIndex:'region', width:110, render:v=>v||<span style={{color:'#ccc'}}>All</span> },
        { title:'Submitted', dataIndex:'submitted_count', width:90,
            render:v=><span style={{fontWeight:700,color:'#1890ff',fontSize:16}}>{v}</span>
        },
        { title:'Last Active', dataIndex:'last_accessed', width:130,
            render:v=>v ? <span style={{fontSize:12}}>{v.split(' ')[0]}</span> : <span style={{color:'#ccc'}}>Never</span>
        },
        { title:'Facilities', render:(_,r)=>(
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                {r.facilities.slice(0,3).map((f,i)=>(
                    <Tooltip key={i} title={`${f.name} — ${f.date?.split(' ')[0]}`}>
                        <Tag color="green" style={{fontSize:10,margin:0}}>{f.name?.split(' ')[0]}</Tag>
                    </Tooltip>
                ))}
                {r.facilities.length > 3 && <Tag style={{fontSize:10,margin:0}}>+{r.facilities.length-3}</Tag>}
                {r.facilities.length === 0 && <span style={{color:'#ccc',fontSize:12}}>None yet</span>}
            </div>
        )},
    ];

    const recentCols = [
        { title:'Facility', dataIndex:'facility_name', render:(t,r)=>(
            <div><div style={{fontWeight:600}}>{t}</div>
            <div style={{fontSize:11,color:'#888'}}>{r.region}</div></div>
        )},
        { title:'Collector', dataIndex:'collector_name', width:150 },
        { title:'Submitted', dataIndex:'submitted_at', width:140,
            render:v=><span style={{fontSize:12}}>{v?.replace('T',' ').slice(0,16)}</span>
        },
    ];

    const pendingCols = [
        { title:'Facility', dataIndex:'name', render:(t,r)=>(
            <div><div style={{fontWeight:600}}>{t}</div>
            <div style={{fontSize:11,color:'#888'}}>{r.region}</div></div>
        )},
    ];

    return (
        <div>
            {/* Filters */}
            <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
                <Select allowClear placeholder="All Collectors" style={{width:200}}
                    value={filterCol} onChange={setFilterCol}>
                    {(data.collectors||[]).map(c=><Option key={c.email} value={c.email}>{c.name}</Option>)}
                </Select>
                <Select allowClear placeholder="All Regions" style={{width:160}}
                    value={filterReg} onChange={setFilterReg}>
                    {['Hhohho','Manzini','Lubombo','Shiselweni'].map(r=><Option key={r} value={r}>{r}</Option>)}
                </Select>
                {(filterCol||filterReg) && <Button onClick={()=>{setFilterCol(null);setFilterReg(null);}}>Clear</Button>}
            </div>

            {/* KPI Cards */}
            {(() => {
                const filteredResponses = (recent_responses||[]).filter(r=>(!filterReg||r.region===filterReg)&&(!filterCol||r.collector_name===(collectors.find(c=>c.email===filterCol)?.name)));
                const filteredCount = filterReg||filterCol ? filteredResponses.length : submitted_count;
                const filteredPct = total_facilities ? Math.round(filteredCount/total_facilities*100) : null;
                return (
            <Row gutter={12} style={{marginBottom:16}}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{borderLeft:'4px solid #1890ff'}}>
                        <Statistic title="Submitted" value={filteredCount}
                            valueStyle={{color:'#1890ff',fontWeight:800,fontSize:28}}
                            suffix={total_facilities ? `/ ${total_facilities}` : ''}/>
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{borderLeft:'4px solid #16a34a'}}>
                        <Statistic title="Coverage" value={filteredPct ?? '—'}
                            suffix={filteredPct !== null ? '%' : ''}
                            valueStyle={{color:'#16a34a',fontWeight:800,fontSize:28}}/>
                        {filteredPct !== null && <Progress percent={filteredPct} showInfo={false} strokeColor="#16a34a" size="small"/>}
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{borderLeft:`4px solid ${days_remaining<=3?'#dc2626':days_remaining<=7?'#d97706':'#7c3aed'}`}}>
                        <Statistic title="Days Remaining" value={days_remaining ?? '—'}
                            valueStyle={{color:days_remaining<=3?'#dc2626':days_remaining<=7?'#d97706':'#7c3aed',fontWeight:800,fontSize:28}}
                            suffix={days_remaining !== null ? 'days' : ''}/>
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{borderLeft:`4px solid ${onTrack===null?'#888':onTrack?'#16a34a':'#dc2626'}`}}>
                        <Statistic title="Pace Needed" value={pace_needed ?? '—'}
                            suffix={pace_needed ? '/day' : ''}
                            valueStyle={{color:onTrack===null?'#888':onTrack?'#16a34a':'#dc2626',fontWeight:800,fontSize:28}}/>
                        {onTrack !== null && (
                            <div style={{fontSize:11,marginTop:4,color:onTrack?'#16a34a':'#dc2626',fontWeight:600}}>
                                {onTrack ? '✅ On track' : '⚠ Behind schedule'}
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
                );
            })()}

            {/* Daily Progress Chart */}
            {dailyDates.length > 0 && (
                <Card size="small" title="📈 Daily Submission Progress" style={{marginBottom:16}}>
                    <ReactECharts option={chartOpt} style={{height:180}}/>
                </Card>
            )}

            {/* Collector Progress */}
            <Card size="small" title={<span><TeamOutlined/> Collector Progress ({collectors.length})</span>}
                style={{marginBottom:16}}>
                <Table size="small" dataSource={collectors.filter(c=>(!filterCol||c.email===filterCol)&&(!filterReg||c.region===filterReg||!c.region))} rowKey="email"
                    columns={collectorCols} pagination={false}/>
            </Card>

            <Row gutter={12}>
                {/* Recent submissions */}
                <Col xs={24} md={14}>
                    <Card size="small" title={<span><CheckCircleOutlined style={{color:'#16a34a'}}/> Recent Submissions</span>}>
                        <Table size="small" dataSource={(recent_responses||[]).filter(r=>(!filterCol||r.collector_name===(collectors.find(c=>c.email===filterCol)?.name))||!filterCol).filter(r=>(!filterReg||r.region===filterReg)||!filterReg)} rowKey="id"
                            columns={recentCols} pagination={{pageSize:8,size:'small'}}/>
                    </Card>
                </Col>

                {/* Pending facilities */}
                {pending_facilities?.length > 0 && (
                    <Col xs={24} md={10}>
                        <Card size="small" title={<span><WarningOutlined style={{color:'#d97706'}}/> Pending Facilities ({pending_facilities.length})</span>}>
                            <Table size="small" dataSource={pending_facilities} rowKey="id"
                                columns={pendingCols} pagination={{pageSize:8,size:'small'}}/>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
}
